import { Controller } from "@hotwired/stimulus"

const CARD_TYPES = [
  { name: "amex", pattern: /^3[47]/, lengths: [15], abbreviation: "AMX" },
  { name: "diners_club_carte_blanche", pattern: /^30[0-5]/, lengths: [14], abbreviation: "" },
  { name: "diners_club_international", pattern: /^36/, lengths: [14], abbreviation: "" },
  { name: "jcb", pattern: /^35(2[89]|[3-8][0-9])/, lengths: [16], abbreviation: "" },
  { name: "laser", pattern: /^(6304|670[69]|6771)/, lengths: [16, 17, 18, 19], abbreviation: "" },
  { name: "visa_electron", pattern: /^(4026|417500|4508|4844|491(3|7))/, lengths: [16], abbreviation: "" },
  { name: "visa", pattern: /^4/, lengths: [16], abbreviation: "VIS" },
  { name: "mastercard", pattern: /^((2[2-7])|(5[1-5]))/, lengths: [16], abbreviation: "ECA" },
  { name: "maestro", pattern: /^(5018|5020|5038|6304|6759|676[1-3])/, lengths: [12, 13, 14, 15, 16, 17, 18, 19], abbreviation: "" },
  { name: "discover", pattern: /^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)/, lengths: [16], abbreviation: "" },
  { name: "myone", pattern: /^600452020/, lengths: [19], abbreviation: "MYO" }
]
const CARD_CLASS_NAMES = CARD_TYPES.map(type => type.name)
const METHODS_WITHOUT_CARD = ["PFP", "PAP", "DIB", "TWI", "DEF", "INT", "APL", "PAY", "NONE"]
const PAYMENT_FIELD_SELECTOR = "input[name='cardno'], input[name='cvv'], input[name='expm'], input[name='expy']"
const WALLET_METHODS = ["APL", "PAY"]
const WALLET_STATUS_ATTEMPTS = 120
const WALLET_STATUS_INTERVAL = 500

export default class extends Controller {
  static targets = [
    "amount",
    "cembrapay",
    "expirationMonth",
    "expirationYear",
    "legalNotice",
    "panel",
    "paymentMethod",
    "paymentOption",
    "requestError",
    "submit",
    "walletButton",
    "walletLoading"
  ]
  static values = {
    cartId: Number,
    cembrapayNotice: String,
    googlePayMerchantId: String,
    inProgressUrl: String,
    postfinancePayUrl: String,
    waitingUrl: String,
    invalidAmount: String,
    invalidCvv: String,
    invalidDate: String,
    invalidNumber: String,
    loading: String,
    merchantId: String,
    needed: String,
    paymentButtonUrl: String,
    requestError: String,
    walletAmount: String,
    walletStatusUrl: String
  }

  connect() {
    this.paymentButtons = new Map()
    this.walletReady = new Set()
    this.element.dataset.paymentFormReady = "true"
    this.restorePaymentSelection()
    this.updateCembrapayNotice()
    this.prepareWalletButtons()
  }

  disconnect() {
    this.request?.abort()
    this.walletRequest?.abort()
    delete this.element.dataset.paymentFormReady
  }

  async submit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }

    this.clearValidationErrors()
    if (!this.paymentFieldsValid()) {
      event.preventDefault()
      return
    }

    // Wallets are authorized by Datatrans' Payment Button, never by the legacy
    // redirect form. This also prevents an Enter key from opening the chooser.
    if (WALLET_METHODS.includes(this.paymentMethodTarget.value)) {
      event.preventDefault()
      return
    }

    event.preventDefault()
    this.setSubmitting(true)
    this.request = new AbortController()

    try {
      const response = await this.lockPayment()
      if (response.status === 409) {
        window.alert(this.invalidAmountValue)
        window.location.reload()
        return
      }
      if (!response.ok) throw new Error(`Payment request failed (${response.status})`)

      if (this.paymentMethodTarget.value === "PFP") {
        const initResponse = await fetch(this.postfinancePayUrlValue, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
            "X-Requested-With": "XMLHttpRequest"
          },
          signal: this.request.signal
        })
        if (!initResponse.ok) throw new Error(`PostFinance Pay initialization failed (${initResponse.status})`)
        const { redirect_url: redirectUrl } = await initResponse.json()
        window.location.assign(redirectUrl)
        return
      }

      this.request = null
      this.element.submit()
    } catch (error) {
      if (error.name !== "AbortError") {
        window.location.assign(this.waitingUrlValue)
      }
    }
  }

  fillDemoCard(event) {
    event.preventDefault()
    if (this.submitting) return

    const option = this.paymentOptionTargets.find(input => input.id === "cart_card_type_pos")
    option.checked = true
    this.selectPayment({ currentTarget: option })
    this.clearValidationErrors()

    const demo = event.currentTarget.dataset
    const values = { cardno: demo.demoCardNumber, expiration_date_visible: demo.demoCardExpiration, cvv: demo.demoCardCvv }
    const panel = this.panelTargets.find(item => item.dataset.ref === option.id)
    Object.entries(values).forEach(([id, value]) => {
      const input = panel.querySelector(`#${id}`)
      input.value = value
      input.dispatchEvent(new Event("input", { bubbles: true }))
    })
  }

  formatExpiration(event) {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4)
    const month = digits.slice(0, 2)
    const year = digits.slice(2, 4)

    event.target.value = `${month}${year ? "/" : ""}${year}`
    this.expirationMonthTarget.value = month
    this.expirationYearTarget.value = year
  }

  validateCardNumber(event) {
    const input = event.currentTarget
    const number = input.value.replace(/[ -]/g, "")
    const cardType = CARD_TYPES.find(type => type.pattern.test(number))

    input.classList.remove("valid", ...CARD_CLASS_NAMES)
    input.dataset.paymentMethod = cardType?.abbreviation || ""
    this.paymentMethodTarget.value = cardType?.abbreviation || ""

    if (!cardType) return

    input.classList.add(cardType.name)
    input.classList.toggle("valid", cardType.lengths.includes(number.length) && this.validLuhn(number))
  }

  selectPayment(event) {
    this.updatePaymentSelection(event.currentTarget)
    this.rememberPaymentSelection(event.currentTarget)
    this.updateCembrapayNotice()
  }

  restorePaymentSelection() {
    let selectedOption = this.paymentOptionTargets.find(option => option.checked)

    try {
      const rememberedId = window.sessionStorage.getItem(this.paymentStorageKey)
      const rememberedOption = this.paymentOptionTargets.find(option => option.id === rememberedId && !option.disabled)
      if (rememberedOption) {
        rememberedOption.checked = true
        selectedOption = rememberedOption
      }
    } catch {
      // Storage can be unavailable in hardened browser privacy modes.
    }

    this.updatePaymentSelection(selectedOption)
  }

  rememberPaymentSelection(option) {
    try {
      window.sessionStorage.setItem(this.paymentStorageKey, option.id)
    } catch {
      // Payment remains usable when storage is unavailable.
    }
  }

  get paymentStorageKey() {
    return `checkout:${this.cartIdValue}:payment-option`
  }

  updatePaymentSelection(option) {
    const selectedPanel = option && this.panelTargets.find(panel => panel.dataset.ref === option.id)

    this.panelTargets.forEach(panel => { panel.hidden = panel !== selectedPanel })
    this.panelTargets.forEach(panel => {
      panel.querySelectorAll(PAYMENT_FIELD_SELECTOR)
        .forEach(input => { input.disabled = panel !== selectedPanel })
    })

    if (!option) return

    if (option.id.startsWith("cart_card_type_cc_")) {
      this.setAliasFields(option)
    } else {
      this.removeAliasFields()
    }

    const cardPaymentMethod = this.element.querySelector("#cardno")?.dataset.paymentMethod || ""
    this.paymentMethodTarget.value = option.value === "POS" ? cardPaymentMethod : option.value
    this.submitTarget.dataset.kind = option.value.toLowerCase()
    this.updatePaymentButtons()
  }

  prepareWalletButtons() {
    if (!this.paymentButtonUrlValue) return

    this.walletButtonTargets.forEach(walletButton => this.prepareWalletButton(walletButton))
  }

  async prepareWalletButton(walletButton) {
    const walletType = walletButton.dataset.walletType
    if (!WALLET_METHODS.includes(walletType)) return

    try {
      await this.loadPaymentButton()
      if (!this.element.isConnected) return

      const paymentButton = new window.PaymentButton()
      this.paymentButtons.set(walletType, paymentButton)
      paymentButton.on("init", () => this.createWalletButton(paymentButton, walletButton))
      paymentButton.on("create", () => {
        this.walletReady.add(walletType)
        this.showWalletPayment(walletType)
        this.updatePaymentButtons()
      })
      paymentButton.on("authorization", data => this.walletAuthorized(data, walletType))
      paymentButton.on("abort", () => this.setSubmitting(false))
      paymentButton.on("unsupported", () => this.disableWalletPayment(walletType))
      paymentButton.on("error", () => this.handleWalletError(walletType))
      paymentButton.init(this.walletConfiguration(walletType))
    } catch {
      this.disableWalletPayment(walletType)
    }
  }

  loadPaymentButton() {
    if (window.PaymentButton) return Promise.resolve()

    const existingScript = document.querySelector(`script[src="${this.paymentButtonUrlValue}"]`)
    if (existingScript) {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener("load", resolve, { once: true })
        existingScript.addEventListener("error", reject, { once: true })
      })
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = this.paymentButtonUrlValue
      script.addEventListener("load", resolve, { once: true })
      script.addEventListener("error", reject, { once: true })
      document.head.append(script)
    })
  }

  createWalletButton(paymentButton, walletButton) {
    paymentButton.create(walletButton, {
      details: {
        total: {
          label: "Veepee.ch",
          amount: { value: this.walletAmountValue, currency: "CHF" }
        }
      },
      options: {
        requestPayerEmail: true,
        requestPayerName: false,
        requestPayerPhone: false,
        requestShipping: false
      },
      transaction: {
        countryCode: "CH",
        refno: String(this.cartIdValue),
        version: 1
      }
    })
  }

  walletConfiguration(walletType) {
    const applePay = walletType === "APL"
    const googlePayConfiguration = {
      buttonType: "long",
      buttonStyle: "black"
    }
    if (this.googlePayMerchantIdValue) {
      googlePayConfiguration.merchantId = this.googlePayMerchantIdValue
    }

    return {
      merchantId: this.merchantIdValue,
      merchantName: "Veepee.ch",
      tokenOnly: false,
      autoSettle: true,
      allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX"],
      useApplePay: applePay,
      useGooglePay: !applePay,
      applePayConfiguration: { buttonType: "plain", buttonStyle: "black" },
      googlePayConfiguration
    }
  }

  updatePaymentButtons() {
    const selectedWallet = this.paymentMethodTarget.value
    const walletSelected = WALLET_METHODS.includes(selectedWallet)
    const walletReady = this.walletReady.has(selectedWallet)

    this.submitTarget.hidden = walletSelected
    this.walletButtonTargets.forEach(walletButton => {
      walletButton.hidden = walletButton.dataset.walletType !== selectedWallet || !walletReady || this.submitting
    })
    this.walletLoadingTarget.hidden = !walletSelected || (walletReady && !this.submitting)
  }

  disableWalletPayment(walletType) {
    const option = this.paymentOptionTargets.find(input => input.value === walletType)
    if (!option) return

    option.disabled = true
    option.closest(".radio")?.classList.add("hidden")
    if (!option.checked) return

    const fallback = this.paymentOptionTargets.find(input => !input.disabled && !WALLET_METHODS.includes(input.value))
    if (!fallback) return

    fallback.checked = true
    this.updatePaymentSelection(fallback)
    this.rememberPaymentSelection(fallback)
  }

  showWalletPayment(walletType) {
    const option = this.paymentOptionTargets.find(input => input.value === walletType)
    option?.closest(".radio")?.classList.remove("hidden")
  }

  handleWalletError(walletType) {
    if (this.paymentMethodTarget.value === walletType) {
      this.walletFailed()
    } else {
      this.disableWalletPayment(walletType)
    }
  }

  async walletAuthorized(data, walletType) {
    if (!data?.transactionId) {
      window.location.assign(this.waitingUrlValue)
      return
    }

    this.setSubmitting(true)
    this.walletRequest = new AbortController()

    try {
      for (let attempt = 0; attempt < WALLET_STATUS_ATTEMPTS; attempt += 1) {
        const response = await this.walletStatus(data.transactionId, walletType)
        if (response.status === 200) {
          const { redirect_url: redirectUrl } = await response.json()
          window.location.assign(redirectUrl)
          return
        }
        if (response.status !== 202) throw new Error(`Wallet payment failed (${response.status})`)
        await new Promise(resolve => window.setTimeout(resolve, WALLET_STATUS_INTERVAL))
      }

      window.location.assign(this.waitingUrlValue)
    } catch (error) {
      if (error.name !== "AbortError") window.location.assign(this.waitingUrlValue)
    }
  }

  walletStatus(transactionId, walletType) {
    const url = new URL(this.walletStatusUrlValue, window.location.origin)
    url.searchParams.set("transaction_id", transactionId)
    url.searchParams.set("payment_method", walletType)

    return fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      signal: this.walletRequest.signal
    })
  }

  walletFailed() {
    window.location.assign(this.waitingUrlValue)
  }

  updateCembrapayNotice() {
    this.element.querySelector("input[name='autoSettle']")?.remove()
    this.legalNoticeTarget.querySelector("[data-cembrapay-notice]")?.remove()

    if (!this.hasCembrapayTarget || !this.cembrapayTarget.checked) return

    const notice = document.createElement("div")
    notice.dataset.cembrapayNotice = "true"
    notice.textContent = this.cembrapayNoticeValue
    this.legalNoticeTarget.append(notice)

    const autoSettle = document.createElement("input")
    autoSettle.type = "hidden"
    autoSettle.name = "autoSettle"
    autoSettle.value = "false"
    this.element.append(autoSettle)
  }

  removeAliasFields() {
    this.element.querySelector("#aliasCC")?.remove()
    this.element.querySelector("input[name='expm'].expiration")?.remove()
    this.element.querySelector("input[name='expy'].expiration")?.remove()
  }

  setAliasFields(option) {
    this.removeAliasFields()

    this.addHiddenField("aliasCC", option.dataset.aliasCc)
    this.addHiddenField("expy", option.dataset.expirationYear, "expiration")
    this.addHiddenField("expm", option.dataset.expirationMonth, "expiration")
  }

  addHiddenField(name, value, className = "") {
    const input = document.createElement("input")
    input.type = "hidden"
    input.id = name === "aliasCC" ? name : `saved_${name}`
    input.name = name
    input.value = value
    input.className = className
    this.element.querySelector(".card-block").prepend(input)
  }

  paymentFieldsValid() {
    const paymentMethod = this.paymentMethodTarget.value
    if (METHODS_WITHOUT_CARD.includes(paymentMethod)) return true

    let valid = true
    const cvv = this.element.querySelector("input[name='cvv']:not(:disabled)")
    const expectedCvvLength = paymentMethod === "AMX" ? 4 : 3

    if (!cvv?.value) {
      this.addValidationError(cvv, this.neededValue)
      valid = false
    } else if (!new RegExp(`^\\d{${expectedCvvLength}}$`).test(cvv.value)) {
      this.addValidationError(cvv, this.invalidCvvValue)
      valid = false
    }

    if (!this.element.querySelector("#aliasCC")) {
      const cardNumber = this.element.querySelector("#cardno")
      const expiration = this.element.querySelector("#expiration_date_visible")

      if (!cardNumber.value) {
        this.addValidationError(cardNumber, this.neededValue)
        valid = false
      } else if (!cardNumber.classList.contains("valid")) {
        this.addValidationError(cardNumber, this.invalidNumberValue)
        valid = false
      }

      if (!this.expirationMonthTarget.value || !this.expirationYearTarget.value) {
        this.addValidationError(expiration, this.neededValue)
        valid = false
      } else if (this.expirationInvalid()) {
        this.addValidationError(expiration, this.invalidDateValue)
        valid = false
      }
    }

    return valid
  }

  clearValidationErrors() {
    this.element.querySelectorAll(".help-inline").forEach(message => message.remove())
    this.element.querySelectorAll(".form-group.error").forEach(group => group.classList.remove("error"))
    this.requestErrorTarget.hidden = true
  }

  addValidationError(input, message) {
    const group = input?.closest(".form-group")
    if (!group) return

    group.classList.add("error")
    const error = document.createElement("small")
    error.className = "help-inline"
    error.textContent = message
    group.append(error)
  }

  expirationInvalid() {
    const century = String(new Date().getFullYear()).slice(0, 2)
    const year = Number(`${century}${this.expirationYearTarget.value}`)
    const month = Number(this.expirationMonthTarget.value)
    if (month < 1 || month > 12) return true

    return new Date(year, month, 0, 23, 59, 59) <= new Date()
  }

  lockPayment() {
    return fetch(this.inProgressUrlValue, {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: new URLSearchParams({ amount: this.amountTarget.value, payment_method: this.paymentMethodTarget.value }),
      signal: this.request.signal
    })
  }

  setSubmitting(submitting) {
    this.submitting = submitting
    this.submitTarget.disabled = submitting
    this.walletButtonTargets.forEach(walletButton => {
      walletButton.querySelectorAll("button").forEach(button => { button.disabled = submitting })
    })
    this.updatePaymentButtons()
    this.submitTarget.setAttribute("aria-busy", String(submitting))
    if (submitting) {
      this.submitTarget.setAttribute("aria-label", this.loadingValue)
    } else {
      this.submitTarget.removeAttribute("aria-label")
    }
  }

  showRequestError() {
    this.requestErrorTarget.textContent = this.requestErrorValue
    this.requestErrorTarget.hidden = false
  }

  validLuhn(number) {
    const sum = [...number].reverse().reduce((total, character, index) => {
      let digit = Number(character)
      if (index % 2 === 1) {
        const doubled = digit * 2
        digit = doubled > 9 ? doubled - 9 : doubled
      }
      return total + digit
    }, 0)

    return sum % 10 === 0
  }
}
